import { useCustomQuery } from "api/useCustomQuery";
import { MeteringByAccountData, MeteringByDetectorData } from "components/account/MeteringInfo";
import { JANZU_URL } from "config/config";

import { API_CACHE_KEYS } from "../cacheKeys";

export type MeteringParams = {
  interval?: string;
  start?: string;
  end?: string;
};

export type GetMeteringByDetectorParams = MeteringParams;

export type GetMeteringByAccountParams = MeteringParams;

const buildMeteringUrl = (endpoint: string, params?: MeteringParams): string => {
  const basePath = `${JANZU_URL}device-api/${endpoint}`;
  const searchParams = new URLSearchParams();

  if (params?.interval !== undefined) {
    searchParams.append("interval", params.interval);
  }
  if (params?.start !== undefined) {
    searchParams.append("start", params.start);
  }
  if (params?.end !== undefined) {
    searchParams.append("end", params.end);
  }

  const queryString = searchParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};

type UseGetMeteringByAccountOptions = {
  params?: GetMeteringByAccountParams;
  options?: {
    enabled?: boolean;
  };
};

type UseGetMeteringByDetectorOptions = {
  params?: GetMeteringByDetectorParams;
  options?: {
    enabled?: boolean;
  };
};

const useGetMeteringByAccount = ({ params, options }: UseGetMeteringByAccountOptions = {}) => {
  const url = buildMeteringUrl("metering-by-account", params);

  return useCustomQuery<MeteringByAccountData[]>(API_CACHE_KEYS.metering.byAccount(params), url, {
    enabled: options?.enabled ?? (!!params?.start && !!params?.end),
  });
};

const useGetMeteringByDetector = ({ params, options }: UseGetMeteringByDetectorOptions = {}) => {
  const url = buildMeteringUrl("metering-by-detector", params);

  return useCustomQuery<MeteringByDetectorData[]>(API_CACHE_KEYS.metering.byDetector(params), url, {
    enabled: options?.enabled ?? (!!params?.start && !!params?.end),
  });
};

type UseMeteringOptions = {
  accountParams?: GetMeteringByAccountParams;
  detectorParams?: GetMeteringByDetectorParams;
  options?: {
    enabled?: boolean;
  };
};

export const useMetering = ({ accountParams, detectorParams, options }: UseMeteringOptions = {}) => {
  const accountMetrics = useGetMeteringByAccount({
    params: accountParams,
    options,
  });
  const detectorMetrics = useGetMeteringByDetector({
    params: detectorParams,
    options,
  });

  return {
    // Account metrics
    accountMetrics: accountMetrics.data,
    isLoadingAccountMetrics: accountMetrics.isFetching,
    accountMetricsError: accountMetrics.error,
    refetchAccountMetrics: accountMetrics.refetch,

    // Detector metrics
    detectorMetrics: detectorMetrics.data,
    isLoadingDetectorMetrics: detectorMetrics.isFetching,
    detectorMetricsError: detectorMetrics.error,
    refetchDetectorMetrics: detectorMetrics.refetch,
  };
};
